import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  CreditCard,
  Eye,
  EyeOff,
  FileText,
  Gift,
  Image as ImageIcon,
  KeyRound,
  Link2,
  Loader2,
  Lock,
  Package,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  Unlock,
  Upload,
  User,
  X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AdminModal, Field, Pill, inputCls, textareaCls } from "@/pages/admin/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import WhatsAppLink from "@/components/WhatsAppLink";
import { formatPrice, formatDate, formatDateTime, PAYMENT_LABELS, ORDER_STATUS } from "@/lib/format";

const TYPE_META = {
  link: { label: "Lien", icon: Link2, tone: "blue" },
  key: { label: "Clé de licence", icon: KeyRound, tone: "amber" },
  text: { label: "Instructions / Texte", icon: FileText, tone: "slate" },
  file: { label: "Fichier", icon: FileText, tone: "green" },
  image: { label: "Image", icon: ImageIcon, tone: "rose" },
};

const fmtSize = (b) =>
  !b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(2)} Go` : b >= 1e6 ? `${(b / 1e6).toFixed(2)} Mo` : `${Math.max(1, Math.round(b / 1e3))} Ko`;

const InfoCard = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-line bg-white p-5">
    <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
      <Icon className="h-4 w-4 text-brand" /> {title}
    </p>
    <div className="mt-3">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 text-xs">
    <span className="text-slate-500">{label}</span>
    <span className="min-w-0 break-words text-right font-semibold text-ink">{value || "—"}</span>
  </div>
);

function AuthImage({ url, className = "", onClick }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let active = true;
    api
      .get(url, { responseType: "blob" })
      .then(({ data }) => active && setSrc(URL.createObjectURL(data)))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [url]);
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 ${className}`}>
        <ImageIcon className="h-5 w-5 text-slate-300" />
      </div>
    );
  }
  return <img src={src} alt="" className={className} onClick={onClick} />;
}

const EMPTY_ITEM = { type: "link", title: "", url: "", value: "", content: "", description: "", file_path: "", file_name: "", file_size: 0, is_hidden: false, expires_at: "", download_limit: 0 };

function ItemFormModal({ open, item, orderId, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_ITEM);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isEdit = Boolean(item?.id);

  useEffect(() => {
    if (open) setForm(item ? { ...EMPTY_ITEM, ...item } : EMPTY_ITEM);
  }, [open, item]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const meta = TYPE_META[form.type] || TYPE_META.link;

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/admin/digital-delivery/${orderId}/upload`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, file_path: data.path, file_name: data.name, file_size: data.size, title: f.title || data.name }));
      toast.success("Fichier téléversé");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = () => {
    if (!form.title.trim()) return toast.error("Le nom de l'élément est requis");
    if (form.type === "link" && !form.url.trim()) return toast.error("Le lien est requis");
    if (form.type === "key" && !form.value.trim()) return toast.error("La valeur de la clé est requise");
    if (form.type === "text" && !form.content.trim()) return toast.error("Le contenu est requis");
    if ((form.type === "file" || form.type === "image") && !form.file_path) return toast.error("Téléversez un fichier");
    onSave(form);
  };

  return (
    <AdminModal open={open} onOpenChange={(v) => !v && onClose()} title={isEdit ? "Modifier l'élément" : "Ajouter un élément"}>
      <div className="space-y-4" data-testid="delivery-item-modal">
        {!isEdit && (
          <div>
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Type d'élément</span>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {Object.entries(TYPE_META).map(([key, m]) => (
                <button
                  key={key}
                  type="button"
                  data-testid={`item-type-${key}`}
                  onClick={() => setForm((f) => ({ ...f, type: key }))}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-bold transition-colors duration-200 ${
                    form.type === key ? "border-brand bg-brand-light text-brand" : "border-line text-slate-600 hover:border-brand/40"
                  }`}
                >
                  <m.icon className="h-4 w-4" /> {m.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <Field label="Nom / Titre *">
          <input data-testid="item-form-title" value={form.title} onChange={set("title")} placeholder="Ex : Télécharger le jeu — Version principale" className={inputCls} />
        </Field>

        {form.type === "link" && (
          <Field label="URL du lien *">
            <input data-testid="item-form-url" value={form.url} onChange={set("url")} placeholder="https://exemple.com/telechargement" className={inputCls} />
          </Field>
        )}

        {form.type === "key" && (
          <Field label="Valeur de la clé / licence *">
            <input data-testid="item-form-value" value={form.value} onChange={set("value")} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" className={`${inputCls} font-mono`} />
          </Field>
        )}

        {form.type === "text" && (
          <Field label="Contenu *">
            <textarea data-testid="item-form-content" value={form.content} onChange={set("content")} rows={5} placeholder="Téléchargez le fichier puis suivez les étapes indiquées…" className={textareaCls} />
          </Field>
        )}

        {(form.type === "file" || form.type === "image") && (
          <div>
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
              {form.type === "image" ? "Image *" : "Fichier *"}
            </span>
            {form.file_path ? (
              <div className="flex items-center gap-3 rounded-xl border border-line bg-slate-50 p-3">
                <meta.icon className="h-5 w-5 shrink-0 text-brand" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-ink">{form.file_name}</p>
                  <p className="text-[11px] text-slate-500">{fmtSize(form.file_size)}</p>
                </div>
                <button
                  type="button"
                  data-testid="item-form-file-clear"
                  onClick={() => setForm((f) => ({ ...f, file_path: "", file_name: "", file_size: 0 }))}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-white hover:text-rose-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="item-form-upload-btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line py-6 text-xs font-bold text-slate-500 transition-colors duration-200 hover:border-brand hover:text-brand disabled:opacity-60"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Téléversement en cours…" : form.type === "image" ? "Téléverser une image (max 8 Mo)" : "Téléverser un fichier (max 20 Mo)"}
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={form.type === "image" ? ".jpg,.jpeg,.png,.webp,.gif" : undefined}
              data-testid="item-form-file-input"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
            />
          </div>
        )}

        <Field label="Description (facultatif)">
          <input data-testid="item-form-description" value={form.description} onChange={set("description")} placeholder="Ex : Téléchargement du fichier principal" className={inputCls} />
        </Field>

        {(form.type === "file" || form.type === "image" || form.type === "link") && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Expire le (vide = accès permanent)">
              <input type="date" data-testid="item-form-expires" value={form.expires_at || ""} onChange={set("expires_at")} className={inputCls} />
            </Field>
            {form.type !== "link" && (
              <Field label="Limite de téléch. (0 = illimité)">
                <input
                  type="number"
                  min="0"
                  data-testid="item-form-limit"
                  value={form.download_limit ?? 0}
                  onChange={(e) => setForm((f) => ({ ...f, download_limit: Math.max(0, parseInt(e.target.value || "0", 10) || 0) }))}
                  className={inputCls}
                />
              </Field>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <button type="button" data-testid="item-form-cancel" onClick={onClose} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-slate-600 hover:text-ink">
            Annuler
          </button>
          <button
            type="button"
            data-testid="item-form-save"
            onClick={submit}
            className="rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-brand/90"
          >
            {isEdit ? "Enregistrer les modifications" : "Ajouter l'élément"}
          </button>
        </div>
      </div>
    </AdminModal>
  );
}

export default function AdminDeliveryDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [zoomUrl, setZoomUrl] = useState("");
  const [downloads, setDownloads] = useState({ items: [], counts: {} });

  const loadDownloads = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/digital-delivery/${id}/downloads`);
      setDownloads(data);
    } catch (err) {
      /* silencieux */
    }
  }, [id]);

  useEffect(() => {
    loadDownloads();
  }, [loadDownloads]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/digital-delivery/${id}`);
      setOrder(data);
    } catch (err) {
      setError(apiError(err, "Commande introuvable"));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) {
    return (
      <div className="rounded-2xl border border-line bg-white p-10 text-center">
        <p className="text-sm font-bold text-ink">{error}</p>
        <Link to="/admin/livraisons" className="mt-4 inline-block rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white">
          Retour aux livraisons
        </Link>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const dd = order.digital_delivery || { items: [], status: "locked" };
  const items = dd.items || [];
  const released = dd.status === "released";
  const claim = order.payment_claim;
  const status = ORDER_STATUS[order.status] || {};
  const fieldEntries = (order.licenses || []).filter((l) => (l.fields || []).length > 0);

  const saveItems = async (nextItems, message = "Contenu enregistré") => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/digital-delivery/${id}/content`, { items: nextItems });
      setOrder((o) => ({ ...o, digital_delivery: data }));
      toast.success(message);
    } catch (err) {
      toast.error(apiError(err, "Enregistrement impossible"));
    } finally {
      setBusy(false);
    }
  };

  const onItemSave = (form) => {
    const next = form.id ? items.map((i) => (i.id === form.id ? { ...i, ...form } : i)) : [...items, form];
    setModal(null);
    saveItems(next, form.id ? "Élément modifié" : "Élément ajouté");
  };

  const move = (index, dir) => {
    const next = [...items];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    saveItems(next, "Ordre mis à jour");
  };

  const toggleHidden = (item) =>
    saveItems(items.map((i) => (i.id === item.id ? { ...i, is_hidden: !i.is_hidden } : i)), item.is_hidden ? "Élément visible" : "Élément masqué");

  const removeItem = (item) => saveItems(items.filter((i) => i.id !== item.id), "Élément supprimé");

  const runConfirm = async () => {
    const action = confirm;
    setConfirm("");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/digital-delivery/${id}/${action}`);
      setOrder(data);
      toast.success(action === "validate" ? "🟢 Commande validée — les contenus sont maintenant accessibles au client" : "Accès révoqué : les contenus sont de nouveau verrouillés");
    } catch (err) {
      toast.error(apiError(err, "Action impossible"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-delivery-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/livraisons" data-testid="back-to-deliveries" className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="font-display text-lg font-extrabold text-ink sm:text-xl">Livraison #{order.order_number}</h1>
            <p className="text-xs text-slate-500">Commandée le {formatDateTime(order.created_at)}</p>
          </div>
        </div>
        <span className={`rounded-full border px-3 py-1.5 text-xs font-bold ${status.className || "border-line text-slate-500"}`}>
          {status.label || order.status}
        </span>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        <div className="space-y-4">
          <InfoCard icon={User} title="Client">
            <Row label="Nom" value={order.customer?.name} />
            <Row label="Email" value={order.customer?.email} />
            <Row label="Téléphone" value={(order.customer?.phone || order.account?.phone) ? <WhatsAppLink phone={order.customer?.phone || order.account?.phone} /> : null} />
            <Row label="WhatsApp" value={(order.account?.whatsapp || order.customer?.phone) ? <WhatsAppLink phone={order.account?.whatsapp || order.customer?.phone} /> : null} />
            <Row label="Pays" value={order.customer?.country} />
            {order.account && <Row label="Compte client" value={order.account.email} />}
            {fieldEntries.map((entry, i) => (
              <div key={i} className="mt-2 border-t border-line pt-2">
                <p className="mb-1 text-[10px] font-bold uppercase text-slate-400">Infos saisies — {entry.product_name}</p>
                {entry.fields.map((f) => (
                  <Row key={f.key} label={f.label} value={f.value} />
                ))}
              </div>
            ))}
          </InfoCard>

          <InfoCard icon={Package} title="Commande">
            <Row label="Numéro" value={order.order_number} />
            <Row label="Date" value={formatDateTime(order.created_at)} />
            {(order.items || []).map((item) => (
              <div key={item.product_id} className="flex items-center justify-between gap-2 border-b border-line py-1.5 text-xs last:border-0">
                <span className="min-w-0 flex-1 truncate font-semibold text-ink">
                  {item.name} <span className="text-slate-400">×{item.quantity}</span>
                </span>
                <span className="font-bold text-ink">{formatPrice(item.line_total, "XOF")}</span>
              </div>
            ))}
            <div className="mt-2 flex justify-between border-t border-line pt-2">
              <span className="text-xs font-bold text-ink">Total</span>
              <span className="font-display text-base font-black text-brand">{formatPrice(order.total, "XOF")}</span>
            </div>
            <Row label="Moyen de paiement" value={PAYMENT_LABELS[order.payment_method] || order.payment_method} />
          </InfoCard>

          <InfoCard icon={CreditCard} title="Paiement">
            <Row label="Montant attendu" value={formatPrice(claim?.amount_expected ?? order.total, "XOF")} />
            {claim ? (
              <>
                <Row label="Moyen déclaré" value={claim.method_name} />
                <Row label="Numéro utilisé" value={claim.phone ? <WhatsAppLink phone={claim.phone} /> : null} />
                <Row label="ID de transaction" value={claim.transaction_id} />
                <Row label="Déclaré le" value={formatDateTime(claim.submitted_at)} />
                <Row
                  label="Vérification"
                  value={
                    claim.status === "verified" ? (
                      <Pill tone="green">Vérifié</Pill>
                    ) : claim.status === "rejected" ? (
                      <Pill tone="rose">Rejeté</Pill>
                    ) : (
                      <Pill tone="amber">À vérifier</Pill>
                    )
                  }
                />
                {claim.proof_path && (
                  <button
                    type="button"
                    data-testid="delivery-view-proof"
                    onClick={() => setProofOpen(true)}
                    className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-line px-3 py-2 text-xs font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
                  >
                    <Eye className="h-3.5 w-3.5" /> Voir la preuve de paiement
                  </button>
                )}
              </>
            ) : (
              <Row label="Déclaration" value={order.payment_status === "paid" ? "Paiement confirmé" : "Aucune déclaration client"} />
            )}
            <Row label="Statut paiement" value={order.payment_status === "paid" ? <Pill tone="green">Payé</Pill> : <Pill tone="amber">Non confirmé</Pill>} />
          </InfoCard>
        </div>

        <div className="space-y-4">
          {released ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="delivery-status-released">
              <div>
                <p className="flex items-center gap-2 text-sm font-bold text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" /> Commande validée — accès client actif
                </p>
                <p className="mt-0.5 text-xs text-emerald-700">
                  Validée le {formatDateTime(dd.validated_at)} par {dd.validated_by}
                </p>
              </div>
              <button
                type="button"
                data-testid="delivery-revoke-btn"
                onClick={() => setConfirm("revoke")}
                disabled={busy}
                className="flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-4 py-2 text-xs font-bold text-rose-600 transition-colors duration-200 hover:bg-rose-50 disabled:opacity-60"
              >
                <Lock className="h-3.5 w-3.5" /> Révoquer l'accès
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="delivery-status-locked">
              <div>
                <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
                  <Lock className="h-4 w-4" /> Accès verrouillé
                </p>
                <p className="mt-0.5 text-xs text-amber-700">
                  Le client ne peut accéder à aucun contenu tant que vous n'avez pas validé la commande.
                </p>
              </div>
              <button
                type="button"
                data-testid="delivery-validate-btn"
                onClick={() => setConfirm("validate")}
                disabled={busy}
                className="flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-black text-white transition-colors duration-200 hover:bg-emerald-700 disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4" /> VALIDER LA COMMANDE
              </button>
            </div>
          )}

          <div className="rounded-2xl border border-line bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-4">
              <div>
                <h2 className="flex items-center gap-2 font-display text-base font-bold text-ink">
                  <Gift className="h-4 w-4 text-brand" /> Contenu de livraison
                </h2>
                <p className="mt-0.5 text-xs text-slate-500">Préparez ce que le client recevra : liens, clés, fichiers, images, instructions.</p>
              </div>
              <button
                type="button"
                data-testid="delivery-add-item-btn"
                onClick={() => setModal({ item: null })}
                className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand/90"
              >
                <Plus className="h-3.5 w-3.5" /> Ajouter un élément
              </button>
            </div>

            <div className="space-y-3 p-5">
              {items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-line py-10 text-center" data-testid="delivery-items-empty">
                  <Gift className="mx-auto h-7 w-7 text-slate-300" />
                  <p className="mt-2 text-xs font-semibold text-slate-500">Aucun contenu préparé pour cette commande.</p>
                  <p className="text-[11px] text-slate-400">Ajoutez un lien, une clé, un fichier, une image ou des instructions.</p>
                </div>
              ) : (
                items.map((item, index) => {
                  const meta = TYPE_META[item.type] || TYPE_META.link;
                  return (
                    <div
                      key={item.id}
                      data-testid={`delivery-item-${item.id}`}
                      className={`rounded-xl border p-3.5 ${item.is_hidden ? "border-dashed border-line bg-slate-50 opacity-60" : "border-line bg-white"}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-slate-50">
                          <meta.icon className="h-5 w-5 text-brand" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-sm font-bold text-ink">{item.title}</span>
                            <Pill tone={meta.tone}>{meta.label}</Pill>
                            {item.is_hidden && <Pill tone="rose">Masqué</Pill>}
                          </div>
                          {item.type === "link" && <p className="mt-0.5 truncate text-xs font-semibold text-brand">{item.url}</p>}
                          {item.type === "key" && <p className="mt-0.5 font-mono text-xs font-bold text-ink">{item.value}</p>}
                          {item.type === "text" && <p className="mt-0.5 line-clamp-2 whitespace-pre-wrap text-xs text-slate-500">{item.content}</p>}
                          {(item.type === "file" || item.type === "image") && (
                            <p className="mt-0.5 text-xs text-slate-500">
                              {item.file_name} · {fmtSize(item.file_size)}
                            </p>
                          )}
                          {item.description && <p className="mt-0.5 text-[11px] text-slate-400">{item.description}</p>}
                          {(item.expires_at || item.download_limit > 0 || downloads.counts[item.id] > 0) && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {item.expires_at && <Pill tone="amber">Expire le {formatDate(item.expires_at)}</Pill>}
                              {item.download_limit > 0 && <Pill tone="blue">Limite : {item.download_limit} téléch.</Pill>}
                              {downloads.counts[item.id] > 0 && (
                                <Pill tone="green">{downloads.counts[item.id]} téléchargement{downloads.counts[item.id] > 1 ? "s" : ""}</Pill>
                              )}
                            </div>
                          )}
                          {item.type === "image" && item.file_path && (
                            <AuthImage
                              url={`/admin/digital-delivery/${id}/file/${item.id}`}
                              className="mt-2 h-20 w-auto max-w-[200px] cursor-zoom-in rounded-lg border border-line object-cover"
                              onClick={() => setZoomUrl(`/admin/digital-delivery/${id}/file/${item.id}`)}
                            />
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-0.5">
                          <button type="button" data-testid={`item-up-${item.id}`} onClick={() => move(index, -1)} disabled={index === 0 || busy} className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-ink disabled:opacity-30">
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" data-testid={`item-down-${item.id}`} onClick={() => move(index, 1)} disabled={index === items.length - 1 || busy} className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-ink disabled:opacity-30">
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" data-testid={`item-hide-${item.id}`} onClick={() => toggleHidden(item)} disabled={busy} title={item.is_hidden ? "Rendre visible" : "Masquer"} className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-ink">
                            {item.is_hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                          <button type="button" data-testid={`item-edit-${item.id}`} onClick={() => setModal({ item })} disabled={busy} className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-ink">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" data-testid={`item-delete-${item.id}`} onClick={() => removeItem(item)} disabled={busy} className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <p className="flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-[11px] font-semibold text-slate-500">
            <Unlock className="h-3.5 w-3.5 shrink-0" />
            Les contenus (liens, clés, fichiers) ne sont accessibles au client qu'après validation de la commande. Chaque téléchargement est vérifié côté serveur.
          </p>

          <div className="rounded-2xl border border-line bg-white" data-testid="delivery-downloads-history">
            <div className="border-b border-line px-5 py-4">
              <h2 className="flex items-center gap-2 font-display text-base font-bold text-ink">
                <ArrowDown className="h-4 w-4 text-brand" /> Historique des téléchargements ({downloads.items.length})
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">Chaque téléchargement client est enregistré avec la date, l'adresse IP et le statut.</p>
            </div>
            <div className="p-5">
              {downloads.items.length === 0 ? (
                <p className="rounded-xl border border-dashed border-line py-6 text-center text-xs font-semibold text-slate-400" data-testid="downloads-history-empty">
                  Aucun téléchargement pour le moment.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-line text-[10px] font-bold uppercase tracking-wide text-slate-400">
                        <th className="py-2 pr-3">Date / Heure</th>
                        <th className="py-2 pr-3">Client</th>
                        <th className="py-2 pr-3">Fichier</th>
                        <th className="py-2 pr-3">IP</th>
                        <th className="py-2">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downloads.items.map((row) => (
                        <tr key={row.id} className="border-b border-line last:border-0" data-testid={`download-row-${row.id}`}>
                          <td className="whitespace-nowrap py-2 pr-3 font-semibold text-ink">{formatDateTime(row.at)}</td>
                          <td className="py-2 pr-3 text-slate-600">{row.customer_email || row.customer_name}</td>
                          <td className="max-w-[200px] truncate py-2 pr-3 font-semibold text-ink">{row.file_name || row.item_title}</td>
                          <td className="whitespace-nowrap py-2 pr-3 font-mono text-slate-500">{row.ip}</td>
                          <td className="py-2">
                            {row.status === "success" ? (
                              <Pill tone="green">Téléchargé</Pill>
                            ) : row.status === "blocked_expired" ? (
                              <Pill tone="rose">Bloqué — expiré</Pill>
                            ) : (
                              <Pill tone="rose">Bloqué — limite</Pill>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ItemFormModal open={Boolean(modal)} item={modal?.item} orderId={id} onClose={() => setModal(null)} onSave={onItemSave} />

      <AlertDialog open={Boolean(confirm)} onOpenChange={(v) => !v && setConfirm("")}>
        <AlertDialogContent data-testid="delivery-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "validate" ? "Valider la commande ?" : "Révoquer l'accès ?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "validate"
                ? items.filter((i) => !i.is_hidden).length > 0
                  ? `Le client pourra immédiatement accéder aux ${items.filter((i) => !i.is_hidden).length} élément(s) de livraison (liens, fichiers, clés). La date et l'heure de validation seront enregistrées.`
                  : "Aucun contenu spécifique à cette commande n'a été préparé : les contenus numériques actifs configurés sur le(s) produit(s) seront automatiquement remis au client. La date et l'heure de validation seront enregistrées."
                : "Le client ne pourra plus accéder aux contenus de cette livraison tant que vous ne l'aurez pas validée à nouveau."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delivery-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delivery-confirm-ok"
              onClick={runConfirm}
              className={confirm === "validate" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-rose-600 text-white hover:bg-rose-700"}
            >
              {confirm === "validate" ? "Oui, valider la commande" : "Oui, révoquer l'accès"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={proofOpen} onOpenChange={setProofOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl p-3" data-testid="delivery-proof-dialog">
          <AuthImage url={`/admin/orders/${id}/payment-proof`} className="max-h-[75vh] w-full rounded-lg object-contain" />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(zoomUrl)} onOpenChange={(v) => !v && setZoomUrl("")}>
        <DialogContent aria-describedby={undefined} className="max-w-4xl p-2">
          {zoomUrl && <AuthImage url={zoomUrl} className="max-h-[80vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
